import React, { Fragment, useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage, FormattedDate } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import InstructionalText from '../notifications/InstructionalText';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// Action creators

import { changeData, clearMethods, fetchAffordability, setMethods } from '@actions/ssap/ssapActionCreators';
import { showModal } from '@actions/common/actionCreators';

// Selectors

import { selectIfEscUIEnabled } from '@reducers/appReducer';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { HouseholdMembersSynchronizer } from '@utils/miscellaneous/HouseholdMembersSynchronizer';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';
import { resolveStartingDate } from '@utils/miscellaneous/resolveStartingDate';
import { pick } from '@utils/miscellaneous/objectHelpers';
import { nowUtil } from '@utils/dates/date.utils';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';

// Constants

import { DATE_FORMAT_DASHED } from '@utils/dates/dateMethods';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { ESC_OFFER_TYPES } from '@constants/ssap/hhmTypes/employerSponsoredCoverageOfferTypes';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { ssapModalTypes as MODAL_TYPES } from '@constants/ssap/modals/ssapModalTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';

// Types

import { IncomeType } from '@components/ssap/SsapTypes';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Enrolled In Other Health Insurance
 *
 */
const Page4_63 = props => {

    const {
        affordability: {
            percent
        },
        data,
        data: {
            coverageYear,
        },
        form,
        formName,
        pageName,
        environment: {
            exchangeName,
            localDates: {
                serverDate
            },
            isState
        },
        householdMembers,
        buttonContainer: ButtonContainer,
        hhmIndex : member,
        familyStatus : {
            primaryTaxFilerHouseholdMembers
        },
        currentHhm,
        page
    } = props;

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const {
        applicantGuid,
        gender,
        name,
        healthCoverage : {
            employerWillOfferInsuranceIndicator
        }
    } = currentHhm;

    const isEscUiEnabled = useSelector(selectIfEscUIEnabled);

    const displayPeriod = (
        <FormattedDate
            value = { resolveStartingDate(serverDate, coverageYear) }
            year = 'numeric'
            month = 'long'
            day = '2-digit'
        />
    );

    useEffect(() => {
        props.actions.fetchAffordability();
    }, []);

    useEffect(() => {
        const newState = { ...data };
        newState.taxHousehold.householdMember[member].escLastReviewDate = nowUtil(DATE_FORMAT_DASHED);
        props.actions.changeData(newState);
    }, []);

    const [localState, setLocalState] = useState({ employerWillOfferInsuranceIndicator: false });

    const { localEmployerWillOfferInsuranceIndicator } = localState;

    useEffect(() => {
        employerWillOfferInsuranceIndicator && checkboxState.length === 0 &&
        props.actions.showModal({
            modalType: MODAL_TYPES.INFORM_TO_ADD_INCOME_SOURCE_MODAL,
            modalData: {
                data: data,
                index: member,
                callback: props.actions.changeData
            }
        });
    }, [employerWillOfferInsuranceIndicator]);

    const onFieldChange = newData => {
        const {
            healthCoverage : {
                employerWillOfferInsuranceIndicator
            },
            employerSponsoredCoverage
        } = newData;
        setLocalState(state => {
            return {
                ...state,
                employerWillOfferInsuranceIndicator: localEmployerWillOfferInsuranceIndicator
            };
        });
        const newState = { ...data };
        if(employerWillOfferInsuranceIndicator === false) {
            newData.hasESI = false;
            employerSponsoredCoverage && employerSponsoredCoverage.length > 0 ? newData.employerSponsoredCoverage = [] : '';
            setCheckboxState(state => state.map((cb) => {
                return { ...cb, btnState: false };
            }));
        }

        newState.taxHousehold.householdMember[member] = { ...newData };
        props.actions.changeData(newState);
    };

    const populateMemberHealthCoverageDetails = () => {
        let collectUniqueMemberEmployer = [];
        let prepareMemberEmployerPair = [];
        let employerCoverageDetail = [];
        const uniqueEmployer = new Set();
        householdMembers.map(member => {
            const { incomes, name } = member;
            employerCoverageDetail = incomes.filter(income => income.type === IncomeType.JOB);
            employerCoverageDetail.map(sn => uniqueEmployer.add(sn.sourceName));
            collectUniqueMemberEmployer = [
                ...collectUniqueMemberEmployer,
                {
                    name: normalizeNameOfPerson(name),
                    applicantGuid: member.applicantGuid,
                    sourceName: [...uniqueEmployer]
                }
            ];
            uniqueEmployer.clear();
        });

        const getButtonState = (emp, applicantGuid) => {
            return (result => result ? result : false)(householdMembers[member].employerSponsoredCoverage
                .find(e => e.companyName === emp && e.applicantGuid === applicantGuid));
        };

        collectUniqueMemberEmployer.map(usn => {
            const { sourceName, applicantGuid } = usn;
            sourceName.map(emp => {
                prepareMemberEmployerPair = [
                    ...prepareMemberEmployerPair,
                    {
                        memberName: usn.name,
                        applicantGuid: applicantGuid,
                        companyName: emp,
                        value: false,
                        btnState: getButtonState(emp, applicantGuid)
                    }
                ];
            });

        });
        return prepareMemberEmployerPair;
    };
    const [checkboxState, setCheckboxState] = useState(populateMemberHealthCoverageDetails());
    const onFieldChange_Checkbox = (...args) => {
        const { indexInArray: index, newData, value } = parseFieldChangeArgs(args);

        setCheckboxState(state => state.map((cb, idx) => idx === index ? { ...cb, btnState: value } : cb));

        const newReduxState = { ...data };
        const entryPoint = newReduxState.taxHousehold.householdMember[member].employerSponsoredCoverage;

        const employerDetailByName = newReduxState.taxHousehold.householdMember.reduce((employerDetail, member) => {
            let existingEmployerDetails = {};
            const hasEmployerCoverage = member.employerSponsoredCoverage.length;

            if (hasEmployerCoverage) {
                const employerDetail = member.employerSponsoredCoverage.find(
                    (employer) => employer.companyName === newData.companyName
                );

                if (employerDetail) {
                    existingEmployerDetails = {
                        address: employerDetail.address,
                        phone: employerDetail.phone,
                        name: employerDetail.name,
                        email: employerDetail.email,
                        ein: employerDetail.ein,
                    };
                }
            }

            return { ...employerDetail, ...existingEmployerDetails };
        }, {});

        value ? entryPoint.push({
            ...cloneObject(EMPTY_OBJECTS.EMPLOYER_DETAILS),
            ...employerDetailByName,
            companyName: newData.companyName,
            memberName: newData.memberName,
            applicantGuid: newData.applicantGuid,
            ...(
                isEscUiEnabled
                    ? {
                        escOfferType: applicantGuid === newData.applicantGuid
                            ? ESC_OFFER_TYPES.SELF_ONLY_COVERAGE
                            : ESC_OFFER_TYPES.FAMILY_COVERAGE
                    } : {}
            )
        }) : entryPoint.splice(entryPoint.findIndex(emp => emp.companyName === newData.companyName && emp.memberName === newData.memberName),1);

        props.actions.changeData(newReduxState);
    };
    const checkAffordability = (income, premiumAmt, frequency) => {
        // premium amount per month
        switch (frequency) {
            case 'YEARLY' :
                premiumAmt = premiumAmt / 12;
                break;
            case 'QUARTERLY':
                premiumAmt = premiumAmt * 4 / 12;
                break;
            case 'WEEKLY':
                premiumAmt = premiumAmt * 52 / 12;
                break;
            case 'BIWEEKLY':
                premiumAmt = premiumAmt * 26 / 12;
                break;
            case 'BIMONTHLY':
                premiumAmt = premiumAmt * 24 / 12;
                break;
            default:
                premiumAmt;
                break;
        }
        return (income * percent / 100) > premiumAmt;
    };
    const calculateMonthlyIncome = (members) => {

        return members
            .map(member => member.annualTaxIncome.projectedIncome > 0 ? member.annualTaxIncome.projectedIncome : member.annualTaxIncome.reportedIncome)
            .reduce((annualHouseholdIncome, annualIncome) => annualHouseholdIncome + annualIncome, 0);
    };

    const onFieldChange_EmployerCoverageInfo = (...args) => {
        let { indexInArray: coverageIndex, name, newData: updatedCoverageDetails, value } = parseFieldChangeArgs(args);

        if(name === 'minimumValuePlan' && value === false) {
            updatedCoverageDetails = {
                ...updatedCoverageDetails,
                employerPremium: EMPTY_OBJECTS.EMPLOYER_DETAILS.employerPremium,
                employerPremiumFrequency: EMPTY_OBJECTS.EMPLOYER_DETAILS.employerPremiumFrequency,
                employeeEnrolled: EMPTY_OBJECTS.EMPLOYER_DETAILS.employeeEnrolled
            };
        }

        const {
            applicantGuid,
            companyName,
            employerPremium,
            employerPremiumFrequency
        } = updatedCoverageDetails;

        const partToSync = pick(
            updatedCoverageDetails,
            [
                'address',
                'ein',
                'email',
                'name',
                'phone'
            ]
        );

        const newState = { ...data };

        const entryPoint = newState.taxHousehold.householdMember[member].employerSponsoredCoverage;

        const annualHouseholdIncome = calculateMonthlyIncome(primaryTaxFilerHouseholdMembers);
        const monthlyIncome = annualHouseholdIncome/12;

        const isEmployerOfferAffordable = employerPremium > 0
            ? checkAffordability(monthlyIncome, employerPremium, employerPremiumFrequency)
            : false;

        entryPoint[coverageIndex] = updatedCoverageDetails;

        newState
            .taxHousehold
            .householdMember[member]
            .employerSponsoredCoverage[coverageIndex]
            .employerOfferAffordable = isEmployerOfferAffordable;

        newState
            .taxHousehold
            .householdMember[member]
            .hasESI = entryPoint.length > 0
                ? entryPoint
                    .some(emp => {
                        const {
                            minimumValuePlan,
                            employerOfferAffordable
                        } = emp;

                        return minimumValuePlan && employerOfferAffordable && employerWillOfferInsuranceIndicator;
                    })
                : false;

        const synchronizer = new HouseholdMembersSynchronizer(householdMembers);
        synchronizer.syncEscCommonData(partToSync, { applicantGuid, companyName });

        newState.taxHousehold.householdMember = synchronizer.getSyncedMembers();

        props.actions.changeData(newState);
    };

    useEffect(() => {
    }, [checkboxState.map(cb => cb.value)]);

    const handleAddEmployerDetailsClick = (...args) => {
        const [ householdMembers, member, chbox, index ] = args;
        const coverageDetails = householdMembers[member].employerSponsoredCoverage.find(emp => emp.companyName === chbox.companyName && emp.memberName === chbox.memberName);

        props.actions.showModal({
            modalType: MODAL_TYPES.ADD_EMPLOYER_COVERAGE_DETAIL_MODAL,
            modalData: {
                actions: {
                    updateCoverageDetails: (...args) => onFieldChange_EmployerCoverageInfo.apply(null, args)
                },
                coverageDetails,
                index,
                pageToReturn: page
            }
        });
    };

    useEffect(() => {
        props.actions.setMethods(
            'Page4_63',
            {
                handleAddEmployerDetailsClick
            }
        );

        return () => {
            props.actions.clearMethods('Page4_63');
        };
    }, []);

    const getContent = (memberInfo, member) => {

        return (
            checkboxState.map((chbox, index) => {
                const {
                    memberName,
                    companyName,
                    value,
                    btnState
                } = chbox;
                const findIndex = memberInfo.employerSponsoredCoverage.findIndex(emp => emp.companyName === chbox.companyName && emp.memberName === chbox.memberName);
                const {
                    applicantGuid: escEntityApplicantGuid,
                    address: {
                        city,
                        postalCode,
                        state,
                        streetAddress1,
                        streetAddress2
                    } = {},
                    phone,
                    minimumValuePlan,
                    employeeEnrolled,
                    employerPremium,
                    employerPremiumFrequency
                } = ~findIndex  ?
                    memberInfo.employerSponsoredCoverage[findIndex] : {};
                return (
                    <li
                        className = 'list__with__btn'
                        key = { `${ memberName }${ companyName }` }
                    >
                        <UniversalInput
                            label = { {
                                text: 'common.input.label.non_internationalized_text',
                                labelTextReplacements: {
                                    text: (
                                        <span className='li__box__label text-break' >
                                            <span className='li__box__label_col margin-l-20 pr-5' >
                                                { companyName }
                                            </span>
                                            <span className='li__box__label_col2' >
                                                { memberName }
                                            </span>
                                        </span>
                                    )
                                },
                                labelClass: [ 'grid-col-12' ]
                            }}
                            inputData = { {
                                isInputRequired: false,
                                classes: [ 'grid-row' ],
                                type: INPUT_TYPES.CHECKBOX,
                                name: 'value',
                                checked: btnState,
                                value: value,
                                currentData: chbox,
                                fields: [],
                                isInputStandalone: true
                            } }
                            inputActions = { {
                                onFieldChange: onFieldChange_Checkbox
                            } }
                            indexInArray = { index }
                        />

                        {
                            btnState && !!~findIndex &&

                            <Fragment>
                                <Fragment>
                                    <ButtonContainer
                                        args = { [ householdMembers, member, chbox, findIndex ] }
                                        type = 'addEmployerCoverageDetailButton'
                                    />
                                    <InstructionalText
                                        Component = {
                                            <span data-instructions = 'instructional_text.coverage_add_employer' />
                                        }
                                    />
                                </Fragment>

                                <div className = 'bordered-b-box' >
                                    <UniversalInput
                                        label = { {
                                            text: isState.NJ
                                                ? 'ssap.input.label.employer_phone'
                                                : 'ssap.input.label.phone',
                                            labelClasses: []
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                                            name: 'phone',
                                            helperClasses: [],
                                            value: phone,
                                            placeholder: 'ssap.input.placeholder.phone',
                                            validationType: VALIDATION_TYPES.PHONE_NUMBER,
                                            currentData: memberInfo.employerSponsoredCoverage[findIndex],
                                            fields: [],
                                            isInputStandalone: true
                                        } }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_EmployerCoverageInfo
                                        } }
                                        indexInArray = { findIndex }
                                    />

                                    <UniversalInput
                                        label = { {
                                            text: isState.NJ
                                                ? 'ssap.input.label.employer_address_line_one'
                                                : 'ssap.input.label.address_line_one',
                                            labelClasses: []
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.REGULAR_INPUT,
                                            name: 'streetAddress1',
                                            helperClasses: [],
                                            value: streetAddress1,
                                            placeholder: 'ssap.input.placeholder.address_line_one',
                                            validationType: VALIDATION_TYPES.ADDRESS_LINE1,
                                            currentData: memberInfo.employerSponsoredCoverage[findIndex],
                                            fields: [ 'address' ],
                                            isInputStandalone: true
                                        } }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_EmployerCoverageInfo
                                        } }
                                        indexInArray = { findIndex }
                                    />

                                    <UniversalInput
                                        label = { {
                                            text: 'ssap.input.label.address_line_two',
                                            labelClasses: []
                                        } }
                                        inputData = { {
                                            isInputRequired: false,
                                            type: INPUT_TYPES.REGULAR_INPUT,
                                            name: 'streetAddress2',
                                            helperClasses: [],
                                            value: streetAddress2,
                                            placeholder: 'ssap.input.placeholder.address_line_two',
                                            validationType: VALIDATION_TYPES.ADDRESS_LINE2,
                                            currentData: memberInfo.employerSponsoredCoverage[findIndex],
                                            fields: [ 'address' ],
                                            isInputStandalone: true
                                        } }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_EmployerCoverageInfo
                                        } }
                                        indexInArray = { findIndex }
                                    />

                                    <UniversalInput
                                        label = { {
                                            text: 'ssap.input.label.city',
                                            labelClasses: []
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.REGULAR_INPUT,
                                            name: 'city',
                                            helperClasses: [],
                                            value: city,
                                            placeholder: 'ssap.input.placeholder.city',
                                            validationType: VALIDATION_TYPES.CITY,
                                            currentData: memberInfo.employerSponsoredCoverage[findIndex],
                                            fields: [ 'address' ],
                                            isInputStandalone: true
                                        } }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_EmployerCoverageInfo
                                        } }
                                        indexInArray = { findIndex }
                                    />

                                    <UniversalInput
                                        label = { {
                                            text: 'ssap.input.label.zip',
                                            labelClasses: []
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.REGULAR_INPUT,
                                            name: 'postalCode',
                                            helperClasses: [],
                                            value: postalCode,
                                            placeholder: 'ssap.input.placeholder.zip',
                                            validationType: VALIDATION_TYPES.ZIP_CODE_5_DIGITS,
                                            currentData: memberInfo.employerSponsoredCoverage[findIndex],
                                            fields: [ 'address' ],
                                            isInputStandalone: true
                                        } }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_EmployerCoverageInfo
                                        } }
                                        indexInArray = { findIndex }
                                    />

                                    <UniversalInput
                                        label = { {
                                            text: 'ssap.input.label.state',
                                            isLabelRequired: true
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.DROPDOWN,
                                            name: 'state',
                                            value: state,
                                            options: DROPDOWN_OPTIONS.STATE,
                                            validationType: VALIDATION_TYPES.CHOSEN,
                                            currentData: memberInfo.employerSponsoredCoverage[findIndex],
                                            fields: [ 'address' ],
                                            isInputStandalone: true
                                        } }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_EmployerCoverageInfo
                                        } }
                                        indexInArray = { findIndex }
                                    />

                                    <UniversalInput
                                        legend = { {
                                            legendText: !isEscUiEnabled
                                                ? 'ssap.input.legend.does_plan_meet_standard'
                                                : applicantGuid === escEntityApplicantGuid
                                                    ? 'ssap.input.legend.does_plan_meet_standard'
                                                    : 'ssap.input.legend.does_family_plan_meet_standard',
                                            legendTextReplacements: {
                                                companyName,
                                                name: normalizeNameOfPerson(name)
                                            },
                                            dataInstructions: 'instructional_text.coverage_minimum_standard',
                                            replacements: {
                                                exchangeName
                                            }
                                        } }
                                        label = { {
                                            ulClass: [ 'margin-b-10' ],
                                            showRequiredIcon: true
                                        } }
                                        inputData = { {
                                            isInputRequired: true,
                                            type: INPUT_TYPES.RADIO_BUTTONS,
                                            name: 'minimumValuePlan',
                                            value: minimumValuePlan,
                                            options: DROPDOWN_OPTIONS.YES_NO,
                                            validationType: VALIDATION_TYPES.CHOSEN,
                                            currentData: memberInfo.employerSponsoredCoverage[findIndex],
                                            fields: [],
                                            isInputStandalone: true
                                        } }
                                        inputActions = { {
                                            onFieldChange: onFieldChange_EmployerCoverageInfo
                                        } }
                                        indexInArray = { findIndex }
                                        errorClasses = { [ 'margin-l-0' ] }
                                    />

                                    {
                                        minimumValuePlan &&

                                        <Fragment>
                                            <InstructionalText
                                                Component = {
                                                    <p
                                                        data-instructions = {
                                                            isState.NJ
                                                                ? null
                                                                : 'instructional_text.employer_coverage_amount'
                                                        }
                                                    >
                                                        {
                                                            !isEscUiEnabled
                                                                ? (
                                                                    (isState.ID || isState.IL || isState.MN || isState.PA || isState.OR)
                                                                        ? 'ssap.additional_info.employer_coverage.premium_amount.feature_off'
                                                                        : 'ssap.additional_info.employer_coverage.premium_amount'
                                                                )
                                                                : applicantGuid === escEntityApplicantGuid
                                                                    ? 'ssap.additional_info.employer_coverage.premium_amount'
                                                                    : 'ssap.additional_info.employer_coverage_family.premium_amount'
                                                        }
                                                    </p>
                                                }
                                                childrenReplacements = { {
                                                    companyName,
                                                    gender,
                                                    memberName: (
                                                        <strong>
                                                            {
                                                                !isEscUiEnabled
                                                                    ? memberName
                                                                    : applicantGuid === escEntityApplicantGuid
                                                                        ? memberName
                                                                        : normalizeNameOfPerson(name)
                                                            }
                                                        </strong>
                                                    )
                                                } }
                                            />
                                            <UniversalInput
                                                label = { {
                                                    text: 'ssap.input.label.total_amount',
                                                    labelClasses: [ 'tablet:grid-col-4' ]
                                                } }
                                                inputData = { {
                                                    isInputRequired: true,
                                                    type: INPUT_TYPES.MONEY_INPUT,
                                                    name: 'employerPremium',
                                                    value: employerPremium,
                                                    isOnlyPositiveAvailable: true,
                                                    inputClasses: [ 'tablet:grid-col-6' ],
                                                    validationType: VALIDATION_TYPES.MONEY,
                                                    placeholder: 'ssap.input.placeholder.amount',
                                                    validationErrorMessage: VALIDATION_ERROR_MESSAGES.DOLLAR,
                                                    currentData: memberInfo.employerSponsoredCoverage[findIndex],
                                                    fields: [],
                                                    isInputStandalone: true
                                                } }
                                                inputActions = { {
                                                    onFieldChange: onFieldChange_EmployerCoverageInfo
                                                } }
                                                indexInArray = { findIndex }
                                            />
                                            <UniversalInput
                                                label = { {
                                                    text: 'ssap.input.label.how_often',
                                                    isLabelRequired: false
                                                } }
                                                inputData = { {
                                                    isInputRequired: true,
                                                    type: INPUT_TYPES.DROPDOWN,
                                                    name: 'employerPremiumFrequency',
                                                    value: employerPremiumFrequency,
                                                    options: DROPDOWN_OPTIONS.FREQUENCY_FOR_EMPLOYER_SPONSORED_COVERAGE,
                                                    validationType: VALIDATION_TYPES.SPECIFIED,
                                                    currentData: memberInfo.employerSponsoredCoverage[findIndex],
                                                    fields: [],
                                                    isInputStandalone: true
                                                } }
                                                indexInArray = { findIndex }
                                                inputActions = { {
                                                    onFieldChange: onFieldChange_EmployerCoverageInfo
                                                } }
                                            />
                                            {isState.MN && <UniversalInput
                                                legend={{
                                                    legendText: 'ssap.input.legend.employer_coverage_enrolled',
                                                    legendTextReplacements: {
                                                        name: normalizeNameOfPerson(name)
                                                    },
                                                }}
                                                label={{
                                                    ulClass: ['margin-b-10'],
                                                    showRequiredIcon: true
                                                }}
                                                inputData={{
                                                    isInputRequired: true,
                                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                                    name: 'employeeEnrolled',
                                                    value: employeeEnrolled,
                                                    options: DROPDOWN_OPTIONS.YES_NO,
                                                    validationType: VALIDATION_TYPES.CHOSEN,
                                                    currentData: memberInfo.employerSponsoredCoverage[findIndex],
                                                    fields: [],
                                                    isInputStandalone: true
                                                }}
                                                inputActions={{
                                                    onFieldChange: onFieldChange_EmployerCoverageInfo
                                                }}
                                                indexInArray={findIndex}
                                                errorClasses={['margin-l-0']}
                                            />}
                                        </Fragment>
                                    }
                                </div>
                            </Fragment>
                        }
                    </li>
                );
            })
        );
    };
    const checkTopLevelValidation = () => {
        const {
            employerWillOfferInsuranceIndicator
        } = householdMembers[member].healthCoverage;
        return !employerWillOfferInsuranceIndicator
            ? true
            : checkboxState.some(emp => emp.btnState);
    };
    return (
        <Fragment>
            <Form
                topLevelValidity = { checkTopLevelValidation() }
                {...{ formName, pageName }}
            >

                <div
                    className = 'fade-in'
                    id = 'aiEmployerCoverageDetail'
                >
                    <div className = 'subsection' >
                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !checkTopLevelValidation() }
                            messageText = { VALIDATION_ERROR_MESSAGES.EMPLOYER }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                        <UniversalInput
                            legend = { {
                                legendText: 'ssap.input.legend.health_insurance_through_job',
                                legendTextReplacements: {
                                    personName: (
                                        <strong>
                                            { normalizeNameOfPerson(name) }
                                        </strong>
                                    ),
                                    displayPeriod
                                }
                            } }
                            label = { {
                                showRequiredIcon: true
                            } }
                            inputData = { {
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'employerWillOfferInsuranceIndicator',
                                value: employerWillOfferInsuranceIndicator,
                                options: DROPDOWN_OPTIONS.YES_NO,
                                validationType: VALIDATION_TYPES.CHOSEN,
                                currentData: householdMembers[member],
                                fields: [ 'healthCoverage' ],
                                isInputStandalone: employerWillOfferInsuranceIndicator

                            } }
                            inputActions = { {
                                onFieldChange: onFieldChange
                            } }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                        {
                            employerWillOfferInsuranceIndicator && checkboxState.length > 0 &&

                            <Fragment>
                                <div className = 'grid-row'>
                                    <fieldset className = 'grid-col-12 usa-fieldset li__box' >
                                        <legend className = 'li__box-legend' >
                                            <span className = 'li__box-legend-header' >
                                                <FormattedMessage 
                                                    id = 'ssap.additional_info.employer_coverage.employer_name'
                                                    tagName = 'strong'
                                                />
                                            </span>
                                            <span className = 'li__box-legend-header margin-l-15' >
                                                <FormattedMessage
                                                    id = 'ssap.additional_info.employer_coverage.employee_name'
                                                    tagName = 'strong'
                                                />
                                            </span>
                                        </legend>
                                        <ul className = 'usa-list usa-list--unstyled' >
                                            {
                                                getContent(householdMembers[member], member)
                                            }
                                        </ul>
                                    </fieldset>
                                </div>
                            </Fragment>
                        }
                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    affordability: state.ssap.affordability,
    data: state.ssap.data,
    form: state.ssap.form,

    household: state
        .ssap
        .data
        .taxHousehold,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    page: state.ssap.page
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        clearMethods,
        fetchAffordability,
        setMethods,
        showModal
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page4_63);
